// ============================================================================
// CASE TYPE HOOKS — Phase 8a
// ============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { caseTypeService } from '../../api/services/caseTypeService';
import type {
  CaseType,
  CaseTypeListItem,
  CreateCaseTypeRequest,
  UpdateCaseTypeRequest,
  CaseTypeSearchRequest,
} from '../../api/types/caseType.types';
import type { ID } from '../../api/types';

export const useCaseTypes = (params?: CaseTypeSearchRequest) => {
  const [data, setData] = useState<CaseTypeListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchCaseTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await caseTypeService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchCaseTypes(); }, [fetchCaseTypes]);

  return { data, loading, error, pagination, refetch: fetchCaseTypes };
};

export const useCaseType = (id: ID | undefined) => {
  const [data, setData] = useState<CaseType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchCaseType = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await caseTypeService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchCaseType(); }, [fetchCaseType]);

  return { data, loading, error, refetch: fetchCaseType };
};

export const useCreateCaseType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createCaseType = async (
    data: CreateCaseTypeRequest,
    options?: { onSuccess?: (item: CaseType) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await caseTypeService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { createCaseType, loading, error };
};

export const useUpdateCaseType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateCaseType = async (
    id: ID, data: UpdateCaseTypeRequest,
    options?: { onSuccess?: (item: CaseType) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await caseTypeService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { updateCaseType, loading, error };
};

// ----------------------------------------------------------------------------
// useCaseTypeOptions — convenience hook for filter/form dropdowns.
// Fetches all ACTIVE case types and returns memoised select options + a code
// → label lookup. Replaces hardcoded CASE_TYPE_OPTIONS / CASE_TYPE_LABELS
// arrays scattered across master/modal screens; ensures the master is the
// single source of truth.
// ----------------------------------------------------------------------------

export interface CaseTypeSelectOption {
  value: string;
  label: string;
}

export const useCaseTypeOptions = () => {
  const { data, loading, error, refetch } = useCaseTypes({
    pageSize: 100,           // small reference master — single page is fine
    sortBy: 'displayOrder',
    sortOrder: 'asc',
    statusCode: 'ACTIVE',
  });

  const options = useMemo<CaseTypeSelectOption[]>(
    () => data.map(t => ({ value: t.caseTypeCode, label: t.displayName })),
    [data],
  );

  const labels = useMemo<Record<string, string>>(
    () => Object.fromEntries(data.map(t => [t.caseTypeCode, t.displayName])),
    [data],
  );

  return { options, labels, loading, error, refetch };
};

export const useDeleteCaseType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteCaseType = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await caseTypeService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { deleteCaseType, loading, error };
};
